import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ContractCard from './components/ContractCard';
import ContractPreviewModal from './components/ContractPreviewModal';
import { contractsData, mainCategoryInfo } from '../../mocks/contracts';
import { Contract } from '../../types/contract';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function ContractsPage() {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);

  // SEO Configuration
  useSEO({
    title: '房仲契約書購買 - 不動產委託銷售契約書訂購 | 專任委託合約 | 房仲買賣契約書 30+專業範本線上購買',
    description: '提供專業的房仲契約書購買與契約書訂購服務，包含不動產委託銷售契約書、專任委託合約、房仲買賣契約書、不動產銷售契約等8大類別，超過30份專業房仲契約書範本供您線上訂購。符合內政部規範的不動產契約書購買，適合房仲公司專用，線上訂購後立即出貨。',
    keywords: '房仲契約書購買,契約書訂購,房仲契約書線上購買,不動產契約書購買,房仲契約書,不動產委託銷售契約書,房仲專用契約書,房仲買賣契約書,專任委託合約,不動產銷售契約,不動產契約書範本,專售房仲公司專用契約書,契約書範本,委託銷售契約書,不動產委託銷售契約書範本',
    canonical: '/contracts',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '房仲契約書購買 - 不動產委託銷售契約書訂購',
          '提供專業的房仲契約書購買與契約書訂購服務，包含不動產委託銷售契約書、專任委託合約、房仲買賣契約書等8大類別',
          '/contracts'
        ),
        generateBreadcrumbSchema([
          { name: '首頁', url: '/' },
          { name: '房仲契約書購買', url: '/contracts' }
        ])
      ]
    }
  });

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const filteredContracts = contractsData.filter(contract => {
    if (selectedCategory !== 'all' && contract.category !== selectedCategory) return false;
    if (searchQuery && !contract.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">房仲契約書購買 - 不動產委託銷售契約書線上訂購</h1>
            <p className="text-lg text-gray-600">提供專業的<strong>房仲契約書購買</strong>與<strong>契約書訂購</strong>服務，包含不動產委託銷售契約書、專任委託合約、一般委託等房仲買賣契約書範本，符合內政部規範的不動產契約書購買，適合房仲公司專用，線上訂購後立即出貨</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                selectedCategory === 'all'
                  ? 'bg-[#d4af37] border-[#d4af37] text-white shadow-lg'
                  : 'bg-white border-gray-200 hover:border-[#d4af37] hover:shadow-md'
              }`}
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
                <i className="ri-file-list-line text-2xl"></i>
              </div>
              <div className="font-bold text-lg">全部契約</div>
            </button>

            {Object.entries(mainCategoryInfo).map(([key, info]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  selectedCategory === key
                    ? 'border-[#d4af37] shadow-lg'
                    : 'bg-white border-gray-200 hover:border-[#d4af37] hover:shadow-md'
                }`}
                style={{
                  backgroundColor: selectedCategory === key ? `${info.color}15` : 'white'
                }}
              >
                <div 
                  className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${info.color}20` }}
                >
                  <i className={`${info.icon} text-2xl`} style={{ color: info.color }}></i>
                </div>
                <div className="font-bold text-lg" style={{ color: selectedCategory === key ? info.color : '#1f2937' }}>
                  {info.name}
                </div>
                {info.description && (
                  <p className="text-sm text-gray-600 mt-2">{info.description}</p>
                )}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-md p-8 mb-12">
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400"></i>
              <input
                type="text"
                placeholder="搜尋契約名稱或關鍵字..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 pl-12 pr-12 rounded-full border-2 border-gray-200 focus:border-[#d4af37] focus:outline-none text-base transition-colors duration-300"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-gray-400"></i>
                </button>
              )}
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              找到 <span className="font-bold text-[#d4af37]">{filteredContracts.length}</span> 份契約範本
            </p>
            {(selectedCategory !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
                className="text-sm text-[#d4af37] hover:text-[#c49d2e] font-medium cursor-pointer whitespace-nowrap"
              >
                清除所有篩選
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredContracts.map(contract => (
              <ContractCard
                key={contract.id}
                contract={contract}
                onPreview={() => setPreviewContract(contract)}
              />
            ))}
          </div>

          {filteredContracts.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500">找不到符合條件的契約範本</p>
              <p className="text-gray-400 mt-2">請嘗試調整篩選條件</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
      {previewContract && (
        <ContractPreviewModal
          contract={previewContract}
          onClose={() => setPreviewContract(null)}
        />
      )}
    </div>
  );
}
